import React, { useState } from 'react';
import type { HilPresentationAction } from '@bizfirst/hil-types';
import { HilDecision, HilPresentationType } from '@bizfirst/hil-types';
import { useHilReader, useHilWriter, useHilSubmit, useHilContext } from '@bizfirst/hil-react';
import { HilProgressBar } from '../shared/HilProgressBar';
import { HilErrorBanner } from '../shared/HilErrorBanner';
import { FormStepRenderer } from '../steps/FormStepRenderer';
import { ApprovalStepRenderer } from '../steps/ApprovalStepRenderer';
import { InfoStepRenderer } from '../steps/InfoStepRenderer';

/**
 * Renders Form/Approval/Info/Custom HIL presentations. Chat is deliberately NOT handled here —
 * @bizfirst/hil-ui-chat-window's HilChatWindow/HilChatComposer is the dedicated chat renderer
 * (multi-turn transcript, re-suspend accumulation, presets), and every current host
 * (see HilObservePanel) already dispatches HilPresentationType.Chat to it directly instead of
 * mounting this component. A prior version had a Chat branch here too, reachable only via the
 * now-removed HilOverlay — dead code, removed rather than kept in sync with two implementations.
 */
export function HilPresentationRenderer(): React.JSX.Element | null {
  const { currentUserID } = useHilContext();
  const reader = useHilReader();
  const writer = useHilWriter();
  const { submit, submitting, error, clearError } = useHilSubmit();
  const [currentStep, setCurrentStep] = useState(1);

  if (!reader || !writer) return null;

  const presentations = reader.getPresentations();
  const totalSteps = reader.getPresentationCount();
  const step = reader.forStep(currentStep);
  const actions = reader.getActions(currentStep);

  if (!step) return null;

  const isLastStep = currentStep === presentations[presentations.length - 1]?.Order;

  const advanceOrSubmit = async (
    action: HilPresentationAction,
    data?: Record<string, unknown>,
    comment?: string,
    reason?: string
  ) => {
    const decision = action.Decision ?? HilDecision.Submitted;

    if (step.presentationType() === HilPresentationType.Form && data) {
      writer.addFormStep({ stepOrder: currentStep, data, actionName: action.ActionKey });
    } else if (step.presentationType() === HilPresentationType.Approval) {
      writer.addApprovalStep({ stepOrder: currentStep, comment, reason, actionName: action.ActionKey });
    }

    if (isLastStep || decision !== '') {
      writer
        .setDecision(decision)
        .setActionName(action.ActionKey)
        .setCurrentUserAsResponder(currentUserID ?? reader.firstActorID() ?? '');
      await submit(writer);
    } else {
      const nextStep = presentations.find(p => p.Order > currentStep);
      if (nextStep) setCurrentStep(nextStep.Order);
    }
  };

  return (
    <div>
      <HilProgressBar current={currentStep} total={totalSteps} />
      {error && <HilErrorBanner message={error} onClose={clearError} />}
      {step.presentationType() === HilPresentationType.Form && (
        <FormStepRenderer
          formID={step.getFormID()}
          formSchemaJson={step.getFormSchemaJson()}
          prePopulate={step.getPrePopulate()}
          promptMessage={step.getPromptMessage()}
          actions={actions}
          disabled={submitting}
          onAction={(action, data) => advanceOrSubmit(action, data)}
        />
      )}
      {step.presentationType() === HilPresentationType.Approval && (
        <ApprovalStepRenderer
          promptMessage={step.getPromptMessage()}
          summaryText={step.getSummaryText()}
          actions={actions}
          disabled={submitting}
          onAction={(action, comment, reason) => advanceOrSubmit(action, undefined, comment, reason)}
        />
      )}
      {step.presentationType() === HilPresentationType.Info && (
        <InfoStepRenderer
          promptMessage={step.getPromptMessage()}
          summaryText={step.getSummaryText()}
          actions={actions}
          disabled={submitting}
          onAction={(action) => advanceOrSubmit(action)}
        />
      )}
      {step.presentationType() === HilPresentationType.Custom && (
        <InfoStepRenderer
          promptMessage={step.getPromptMessage() ?? 'Custom interaction — rendered by your app.'}
          summaryText={step.getSummaryText()}
          actions={actions}
          disabled={submitting}
          onAction={(action) => advanceOrSubmit(action)}
        />
      )}
    </div>
  );
}
